/**
 * The weather behind the app.
 *
 * One engine, ten kinds of weather - not ten effects. A scene says "petal"
 * or "ember" and how many; everything else here is shared, and adding a
 * scene never means adding an animation.
 *
 * There is no loop. Each particle is one small element with half a dozen
 * custom properties on it, and a CSS keyframe animation that runs entirely
 * on the compositor: the main thread does nothing at all once the elements
 * exist, which is the difference between weather you can afford and weather
 * you notice in the fan. Twelve to twenty elements at the very most, built
 * once per scene and never rebuilt.
 *
 * The whole layer is decoration: aria-hidden, untouchable by the pointer,
 * paused while the window is hidden, and not rendered at all for anyone who
 * asked for less motion.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { scene, type ParticleType } from '@/lib/themes';
import { drawingScene, watchScene } from '@/lib/wallpaper';

type Shape = 'round' | 'petal' | 'diamond' | 'square' | 'streak' | 'blob';
type Path = 'pugo-fall' | 'pugo-rise' | 'pugo-float';

type Spec = {
  shape: Shape;
  path: Path;
  /** Size in pixels, smallest to largest. */
  size: [number, number];
  /** How long one crossing takes, in seconds. */
  dur: [number, number];
  /** How far it wanders sideways on the way, in pixels. */
  sway: [number, number];
  /** Brightest opacity it reaches. */
  peak: [number, number];
  /** Degrees turned over one crossing. Zero for anything that should not spin. */
  spin: number;
  tints: string[];
};

/*
 * The ten kinds of weather.
 *
 * The tints are the particle's own - a petal is pink in every scene that
 * uses petals - because this is the scene's weather rather than part of its
 * palette. Every peak is low on purpose: this layer should be something you
 * notice on the second look, never on the first.
 */
const SPECS: Record<ParticleType, Spec> = {
  petal:   { shape: 'petal',   path: 'pugo-fall',  size: [7, 13],  dur: [26, 46], sway: [30, 90],  peak: [0.10, 0.24], spin: 360, tints: ['#FFB7D5', '#F7A8C8', '#FFD9E8'] },
  mist:    { shape: 'blob',    path: 'pugo-float', size: [60, 130], dur: [26, 44], sway: [40, 110], peak: [0.05, 0.10], spin: 0,   tints: ['#6DF7CF', '#4EB7D4'] },
  dust:    { shape: 'round',   path: 'pugo-float', size: [2, 4],   dur: [20, 38], sway: [60, 150], peak: [0.12, 0.28], spin: 0,   tints: ['#E9A063', '#C98450', '#F2C58A'] },
  star:    { shape: 'round',   path: 'pugo-float', size: [2, 3],   dur: [22, 40], sway: [18, 60],  peak: [0.20, 0.46], spin: 0,   tints: ['#C9B6FF', '#9FD4FF', '#FFFFFF'] },
  pixel:   { shape: 'square',  path: 'pugo-fall',  size: [2, 3],   dur: [24, 40], sway: [8, 26],   peak: [0.16, 0.36], spin: 0,   tints: ['#7CFFB2', '#3FE08A'] },
  crystal: { shape: 'diamond', path: 'pugo-float', size: [3, 5],   dur: [20, 34], sway: [24, 70],  peak: [0.16, 0.38], spin: 180, tints: ['#FFA2DF', '#72E3F6', '#FFFFFF'] },
  ember:   { shape: 'round',   path: 'pugo-rise',  size: [2, 4],   dur: [14, 26], sway: [20, 60],  peak: [0.14, 0.32], spin: 0,   tints: ['#FF7A3C', '#FF4438', '#FFB07A'] },
  digital: { shape: 'streak',  path: 'pugo-fall',  size: [1, 2],   dur: [8, 16],  sway: [0, 6],    peak: [0.10, 0.22], spin: 0,   tints: ['#A6FF4D', '#6EDB2E'] },
  firefly: { shape: 'round',   path: 'pugo-float', size: [3, 5],   dur: [16, 30], sway: [50, 130], peak: [0.14, 0.42], spin: 0,   tints: ['#FFD98A', '#9BE86B'] },
  cosmic:  { shape: 'round',   path: 'pugo-float', size: [2, 3],   dur: [24, 44], sway: [16, 52],  peak: [0.16, 0.40], spin: 0,   tints: ['#FF4D6D', '#FF8FA3', '#FFFFFF'] }
};

type Particle = {
  key: number;
  shape: Shape;
  style: Record<string, string>;
};

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T,>(list: T[]): T => list[Math.floor(Math.random() * list.length)] as T;

/** True when the system or the in-app setting asks for less motion. */
function lessMotion(): boolean {
  return document.body.classList.contains('reduce-motion')
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * How many to draw here.
 *
 * The scene asks for a desktop window on ordinary hardware. A phone, a
 * narrow window or a machine with few cores gets fewer - this is
 * atmosphere, and atmosphere is the first thing that should go.
 */
function howMany(want: number, width: number): number {
  let n = Math.round(want * 0.7);
  if (width < 1500) n = Math.round(n * 0.8);
  if (width < 900) n = Math.round(n * 0.6);
  if ((navigator.hardwareConcurrency || 8) <= 4) n = Math.round(n * 0.6);
  /* Twelve is already more than anybody counts, and every one of these is a
     layer the compositor keeps and moves for as long as the app is open. */
  return Math.max(3, Math.min(12, n));
}

/**
 * Where one particle starts and how it travels.
 *
 * Two thirds of them are born in the outer fifth of the window: the middle
 * is where the friends list, the chat and every form live, and the edges
 * are where the wallpaper is actually visible. The negative delay is what
 * stops the whole set arriving together - each one starts mid-flight.
 */
function build(spec: Spec, i: number): Particle {
  const edge = Math.random() < 0.66;
  const left = Math.random() < 0.5;
  const x = edge ? (left ? rand(0, 20) : rand(80, 100)) : rand(0, 100);
  const dur = rand(spec.dur[0], spec.dur[1]);
  const size = rand(spec.size[0], spec.size[1]);
  const spin = spec.spin ? rand(-spec.spin, spec.spin) : 0;
  /* A diamond is a square turned forty-five degrees; giving it that in the
     same property the animation writes keeps it to one transform. */
  const base = spec.shape === 'diamond' ? 45 : 0;

  return {
    key: i,
    shape: spec.shape,
    style: {
      '--x': x.toFixed(2) + '%',
      '--size': size.toFixed(1) + 'px',
      '--tint': pick(spec.tints),
      '--dur': dur.toFixed(1) + 's',
      '--delay': (-rand(0, dur)).toFixed(1) + 's',
      '--sway': Math.round(rand(spec.sway[0], spec.sway[1])) * (left ? 1 : -1) + 'px',
      '--peak': rand(spec.peak[0], spec.peak[1]).toFixed(3),
      '--path': spec.path,
      '--spin0': base + 'deg',
      '--spin1': (base + spin / 2).toFixed(0) + 'deg',
      '--spin2': (base + spin).toFixed(0) + 'deg'
    }
  };
}

export function Ambience() {
  const id = useSyncExternalStore(watchScene, drawingScene, () => null);
  const ref = useRef<HTMLDivElement | null>(null);

  /* Only to rebuild the set when the window changes size band - the layer
     itself is sized by CSS and never measured. */
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    let last = window.innerWidth < 900;
    const onResize = () => {
      const narrow = window.innerWidth < 900;
      if (narrow !== last) { last = narrow; setWidth(window.innerWidth); }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const s = id ? scene(id) : null;
  const still = typeof document !== 'undefined' && lessMotion();

  /* Built once per scene. Nothing recreates these while the scene stands. */
  const particles = useMemo(() => {
    if (!s || still) return [];
    const spec = SPECS[s.effects.ambientParticleType];
    const n = howMany(s.effects.ambientParticleAmount, width);
    const out: Particle[] = [];
    for (let i = 0; i < n; i++) out.push(build(spec, i));
    return out;
  }, [s, still, width]);

  /*
   * A hidden window draws nothing.
   *
   * animation-play-state on the container's children costs one attribute
   * write, and the compositor stops there - no timers, no loop, nothing to
   * clean up beyond the listener.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    /* Hidden is not the only way to stop looking at something. A window
       left open behind the game you are playing is still compositing every
       one of these, sixty times a second, for nobody - so losing focus
       stops them too, and a press or a keystroke brings them back. */
    const sync = () => { el.dataset.paused = document.hidden || !document.hasFocus() ? '1' : '0'; };
    sync();
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('blur', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('blur', sync);
    };
  }, [particles.length]);

  /*
   * Portalled to the body so it lands beside the wallpaper layers rather
   * than inside the app's own stacking context: above the photograph, below
   * every panel.
   */
  return createPortal(
    <div
      id="pugo-ambience"
      ref={ref}
      aria-hidden="true"
      data-on={particles.length ? '1' : '0'}
      /* Per scene, so React replaces the set rather than reusing elements
         mid-flight and making them jump. */
      key={s ? s.id : 'none'}
    >
      {particles.map((p) => (
        <span
          key={p.key}
          className="pugo-p"
          data-shape={p.shape}
          style={p.style as React.CSSProperties}
        />
      ))}
    </div>,
    document.body
  );
}
